/**
 * VP-231 — parsing of the backend error envelope `{ code, message, issues? }`.
 *
 * The backend localizes `message` server-side (see apps/api/utils/i18n.ts okBody/errorBody)
 * precisely so the frontend can show it verbatim. Dependency-free (no i18n import) so the
 * classification rules are unit-testable outside the browser bundle.
 */

export type ParsedServerBody = {
  code?: string;
  message?: string;
  /** true when the body carries a zod `issues` array — a genuine form-validation failure */
  hasIssues: boolean;
};

/** Backend code for a real request-validation failure (MessageCode.VALIDATION_FAILED). */
const VALIDATION_FAILED_CODE = "WARN_VAL_010";

export function parseServerErrorBody(rawBody: string | null): ParsedServerBody | null {
  if (!rawBody) return null;
  try {
    const json: unknown = JSON.parse(rawBody);
    if (typeof json !== "object" || json === null) return null;
    const obj = json as Record<string, unknown>;
    return {
      code: typeof obj.code === "string" ? obj.code : undefined,
      message: typeof obj.message === "string" ? obj.message : undefined,
      hasIssues: Array.isArray(obj.issues),
    };
  } catch {
    return null;
  }
}

/**
 * Is this 400-family body an actual form-validation failure? Only then should the UI say
 * "check the form fields". Unparseable bodies keep the legacy validation copy (we know
 * nothing better to show); parsed bodies with a different code (e.g. NO_TOKENS) are not
 * form problems and should surface their own localized message instead.
 */
export function isValidationBody(body: ParsedServerBody | null): boolean {
  if (body === null) return true;
  return body.hasIssues || body.code === VALIDATION_FAILED_CODE;
}
